import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

function Products() {
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [fadeKey, setFadeKey] = useState(0);
  const productsPerPage = 6;

  const categories = useMemo(
    () => [
      "All",
      "Chairs",
      "Barstools",
      "Dining",
      "Consoles",
      "Occasional Tables",
      "Mesquite",
      "Office Furniture",
    ],
    []
  );

  const [category, setCategory] = useState(() => {
    const initial = searchParams.get("category");
    return initial && categories.includes(initial) ? initial : "All";
  });

  useEffect(() => {
    fetch("/products.json")
      .then((r) => r.json())
      .then(setProducts)
      .catch((e) => console.error("Failed to load products", e));
  }, []);

  // keep URL in sync when category changes
  useEffect(() => {
    if (category === "All") {
      searchParams.delete("category");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ category }, { replace: true });
    }
  }, [category]); // eslint-disable-line

  // if user visits /products?category=Something
  useEffect(() => {
    const c = searchParams.get("category");
    if (c && categories.includes(c) && c !== category) {
      setCategory(c);
      setCurrentPage(1);
      setFadeKey((k) => k + 1);
    }
  }, [searchParams]); // eslint-disable-line

  const categoryDescriptions = {
    All: "Browse our full collection of rustic furniture and décor, each piece built with timeless Texas charm and quality.",
    Chairs: "Wide selection of chairs, from armchairs, side chairs, wingbacks, swivel chairs, and more. Ask about custom options.",
    Barstools: "Wide selection of barstools, perfect for kitchen islands, home bars, and game rooms. Ask about custom options.",
    Consoles:
      "Handmade wood consoles that blend rustic character with modern function, ideal for entryways, living rooms, or entertainment spaces.",
    "Occasional Tables":
      "Our occasional tables collection includes end tables, sofa tables, coffee tables, and accent pieces—each designed to add warmth, storage, and Western character to any room. Available in mesquite, cowhide, copper inlay, and custom finishes so you can match your space perfectly.",
    "Bed Sets":
      "Classic designs to rustic designs, with great deals on bed sets that include matching nightstands and dressers.",
    Mesquite:
      "Our signature mesquite furniture showcases the beauty of native Texas wood, each piece with unique grain patterns and natural warmth. Ask about custom options.",
    Dining:
      "Wide selection of solid wood dining tables and dining sets, either made with copper, mesquite, or poderosa pine, built to last for generations. Ask about custom options.",
    "Office Furniture":
      "Explore our rustic office furniture — including executive desks, credenzas, bookcases and file cabinets — built with rustic style and durability for your workspace. Custom options available for stain.",
  };

  const handleCategoryChange = (cat) => {
    setCategory(cat);
    setCurrentPage(1);
    setFadeKey((k) => k + 1);
  };

  const filtered =
    category === "All"
      ? products
      : products.filter((p) => p.categories && p.categories.includes(category));

  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;
  const currentProducts = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / productsPerPage);

  return (
    <main className="pt-24 max-w-7xl mx-auto px-6">
      <h2 className="text-4xl font-bold text-center text-brand-red mb-12">
        Our Products
      </h2>

      {/* Filter Buttons */}
      <div className="flex justify-center flex-wrap gap-4 mb-6">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryChange(cat)}
            className={`px-5 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md ${
              category === cat
                ? "bg-brand-red text-white shadow-lg scale-105"
                : "bg-white text-brand-red border border-brand-red hover:bg-brand-red hover:text-white hover:shadow-lg"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Category Description */}
      <div
        key={fadeKey}
        className="text-center text-gray-700 text-lg max-w-3xl mx-auto mb-12 animate-fadeIn"
      >
        {categoryDescriptions[category]}
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {currentProducts.map((p) => (
          <Link
            key={p.id}
            to={`/products/${p.id}`}
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-transform duration-500 hover:-translate-y-2 block"
          >
            <div className="flex items-center justify-center bg-gray-100 h-64 overflow-hidden">
              <img
                src={p.images[0]}
                alt={p.name}
                className="max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-6 text-center">
              <h4 className="text-xl font-semibold text-brand-red mb-1">
                {p.name}
              </h4>
              <p className="text-gray-700 font-medium mb-2">
                {p.price ? p.price : "Custom Order Available"}
              </p>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {p.shortDesc}
              </p>
              <span className="text-brand-red font-semibold hover:underline hover:text-red-800 transition-all duration-300">
                View Details →
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-12 space-x-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                currentPage === page
                  ? "bg-brand-red text-white shadow-lg scale-105"
                  : "bg-white border border-brand-red text-brand-red hover:bg-brand-red hover:text-white"
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </main>
  );
}

export default Products;